import React, { useState, useEffect } from 'react'
import { simulacionesGpsService } from '../services/simulacionesGpsService'
import { toast } from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { formatearFechaParaMostrar } from '../utils/dateUtils'
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Copy,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  RotateCcw,
  Route,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

const GestionSimulacionesGPS = () => {
  const { hasPermission } = useAuth()
  const [simulaciones, setSimulaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSimulacion, setSelectedSimulacion] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filtros, setFiltros] = useState(() => {
    const saved = localStorage.getItem('gps_filtros')
    return saved ? JSON.parse(saved) : {
      tipo: 'TODOS',
      estado: '',
      fecha_inicio: '',
      fecha_fin: '',
      busqueda: ''
    }
  })
  const [ordenamiento, setOrdenamiento] = useState({
    campo: 'id',
    direccion: 'desc'
  })

  // Cargar simulaciones
  const cargarSimulaciones = async () => {
    try {
      setLoading(true)
      const response = await simulacionesGpsService.getSimulaciones(filtros)
      setSimulaciones(response.data)
    } catch (error) {
      console.error('Error cargando simulaciones:', error)
      toast.error('Error al cargar las simulaciones')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    cargarSimulaciones()
    // Guardar filtros en localStorage
    localStorage.setItem('gps_filtros', JSON.stringify(filtros))
  }, [filtros])

  // Cargar simulación por ID
  const cargarSimulacion = async (id) => {
    try {
      const response = await simulacionesGpsService.getSimulacion(id)
      setSelectedSimulacion(response.data)
      setShowModal(true)
    } catch (error) {
      console.error('Error cargando simulación:', error)
      toast.error('Error al cargar la simulación')
    }
  }

  // Eliminar simulación
  const eliminarSimulacion = async () => {
    try {
      await simulacionesGpsService.deleteSimulacion(deleteId)
      toast.success('Simulación eliminada exitosamente')
      setShowDeleteModal(false)
      setDeleteId(null)
      cargarSimulaciones()
    } catch (error) {
      console.error('Error eliminando simulación:', error)
      toast.error('Error al eliminar la simulación')
    }
  }

  // Duplicar simulación
  const duplicarSimulacion = async (id) => {
    try {
      // Obtener los datos de la simulación
      const response = await simulacionesGpsService.getSimulacion(id)
      const simulacionData = response.data
      
      // Redirigir al formulario con los datos copiados
      window.location.href = `/simulaciones-gps/nueva?copiar=${id}`
    } catch (error) {
      console.error('Error obteniendo simulación para copiar:', error)
      toast.error('Error al obtener los datos de la simulación')
    }
  }

  // Obtener estado visual
  const getEstadoVisual = (simulacion) => {
    const fechaFinal = new Date(simulacion.fechaHoraFinal)
    const ahora = new Date()
    
    if (fechaFinal > ahora) {
      return { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bgColor: 'bg-green-100',
        text: 'Activo'
      }
    } else {
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bgColor: 'bg-red-100',
        text: 'Inactivo'
      }
    }
  }

  // Obtener tipo de simulación visual
  const getTipoSimulacionVisual = (tipo) => {
    const tipos = {
      'REALTIME': { color: 'text-blue-600', bgColor: 'bg-blue-100', text: 'Tiempo Real' },
      'PASTTIME': { color: 'text-orange-600', bgColor: 'bg-orange-100', text: 'Tiempo Pasado' },
      'REPLICA': { color: 'text-purple-600', bgColor: 'bg-purple-100', text: 'Réplica' }
    }
    return tipos[tipo] || { color: 'text-gray-600', bgColor: 'bg-gray-100', text: tipo }
  }

  // Manejar ordenamiento
  const handleOrdenamiento = (campo) => {
    setOrdenamiento(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc'
    }))
  }

  // Obtener icono de ordenamiento
  const getOrdenamientoIcono = (campo) => {
    if (ordenamiento.campo !== campo) {
      return <ChevronsUpDown className="w-4 h-4" />
    }
    return ordenamiento.direccion === 'asc' 
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />
  }

  // Resetear página cuando cambien filtros o ordenamiento
  useEffect(() => {
    setCurrentPage(1)
  }, [filtros, ordenamiento])

  // Filtrar y ordenar simulaciones
  const simulacionesFiltradas = simulaciones.filter(simulacion => {
    // Filtro por tipo
    if (filtros.tipo !== 'TODOS' && simulacion.tipoSimulacion !== filtros.tipo) {
      return false
    }
    
    // Filtro por estado
    if (filtros.estado !== '' && simulacion.estado !== filtros.estado) {
      return false
    }
    
    // Filtro por fecha inicio
    if (filtros.fecha_inicio) {
      const fechaInicio = new Date(filtros.fecha_inicio)
      const fechaSimulacion = new Date(simulacion.fechaHoraInicial)
      if (fechaSimulacion < fechaInicio) {
        return false
      }
    }
    
    // Filtro por fecha fin
    if (filtros.fecha_fin) {
      const fechaFin = new Date(filtros.fecha_fin)
      const fechaSimulacion = new Date(simulacion.fechaHoraFinal)
      if (fechaSimulacion > fechaFin) {
        return false
      }
    }
    
    // Filtro por búsqueda
    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase()
      const campos = [
        simulacion.id?.toString(),
        simulacion.imei?.toString(),
        simulacion.placa,
        simulacion.nombre_ruta,
        simulacion.tipoSimulacion
      ].filter(Boolean)
      
      if (!campos.some(campo => campo.toLowerCase().includes(busqueda))) {
        return false
      }
    }
    
    return true
  }).sort((a, b) => {
    let valA = a[ordenamiento.campo]
    let valB = b[ordenamiento.campo]
    
    // Manejar valores nulos/undefined
    if (valA === null || valA === undefined) valA = ''
    if (valB === null || valB === undefined) valB = ''
    
    // Tratamiento especial para el campo ID (como número entero)
    if (ordenamiento.campo === 'id') {
      valA = parseInt(valA) || 0
      valB = parseInt(valB) || 0
      
      if (valA < valB) return ordenamiento.direccion === 'asc' ? -1 : 1
      if (valA > valB) return ordenamiento.direccion === 'asc' ? 1 : -1
      return 0
    }
    
    // Para otros campos, convertir a string para comparación
    if (typeof valA !== 'string') valA = String(valA)
    if (typeof valB !== 'string') valB = String(valB)
    
    // Comparar
    if (valA < valB) return ordenamiento.direccion === 'asc' ? -1 : 1
    if (valA > valB) return ordenamiento.direccion === 'asc' ? 1 : -1
    return 0
  })

  // Calcular paginación
  const ITEMS_PER_PAGE = 10
  const totalPages = Math.ceil(simulacionesFiltradas.length / ITEMS_PER_PAGE)
  const simulacionesPagina = simulacionesFiltradas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-gray-600">Administra las simulaciones de rutas GPS</p>
        </div>
        <div className="flex gap-2">
          {hasPermission('AGREGAR_SIMULACION_GPS') && (
          <button
            onClick={() => window.location.href = '/simulaciones-gps/nueva'}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nueva Simulación
          </button>
          )}
          {hasPermission('VER_REPLICAS_GPS') && (
          <button
            onClick={() => window.location.href = '/simulaciones-gps/replicas'}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Copy className="w-5 h-5" />
            Ver Réplicas
          </button>
          )}
          {hasPermission('SIMULAR_POSICION_GPS') && (
          <button
            onClick={() => window.location.href = '/simulaciones-gps/simular-posicion'}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <MapPin className="w-5 h-5" />
            Simular Posición
          </button>
          )}
          {hasPermission('GENERAR_RUTA_GPS') && (
          <button
            onClick={() => window.location.href = '/simulaciones-gps/generar-ruta'}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Route className="w-5 h-5" />
            Generar Ruta
          </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
              <span className="ml-1 text-gray-400" title="Tipo de simulación: REALTIME = Tiempo real, PASTTIME = Tiempo pasado, REPLICA = Réplica">
                ⓘ
              </span>
            </label>
            <select
              value={filtros.tipo}
              onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="TODOS">Todos los tipos</option>
              <option value="REALTIME">Tiempo Real</option>
              <option value="PASTTIME">Tiempo Pasado</option>
              <option value="REPLICA">Réplica</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
              <span className="ml-1 text-gray-400" title="Estado de la simulación: Activo = Ejecutándose, Inactivo = Pausada">
                ⓘ
              </span>
            </label>
            <select
              value={filtros.estado}
              onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="1">Activo</option>
              <option value="0">Inactivo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Inicio
              <span className="ml-1 text-gray-400" title="Filtrar simulaciones desde F. Final">
                ⓘ
              </span>
            </label>
            <input
              type="datetime-local"
              value={filtros.fecha_inicio}
              onChange={(e) => setFiltros({...filtros, fecha_inicio: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Fin
              <span className="ml-1 text-gray-400" title="Filtrar simulaciones hasta F. Final">
                ⓘ
              </span>
            </label>
            <input
              type="datetime-local"
              value={filtros.fecha_fin}
              onChange={(e) => setFiltros({...filtros, fecha_fin: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Búsqueda
              <span className="ml-1 text-gray-400" title="Buscar en IMEI, placa, nombre de ruta y otros campos">
                ⓘ
              </span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Placa, IMEI, nombre..."
                value={filtros.busqueda}
                onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
                className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                const filtrosLimpios = {
                  tipo: 'TODOS',
                  estado: '',
                  fecha_inicio: '',
                  fecha_fin: '',
                  busqueda: ''
                }
                setFiltros(filtrosLimpios)
                localStorage.removeItem('gps_filtros')
              }}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('id')}
                  >
                    <div className="flex items-center gap-1">
                      ID
                      {getOrdenamientoIcono('id')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('fechaLanzamiento')}
                  >
                    <div className="flex items-center gap-1">
                      F. Ejecución
                      {getOrdenamientoIcono('fechaLanzamiento')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('fechaHoraInicial')}
                  >
                    <div className="flex items-center gap-1">
                      F. Inicio
                      {getOrdenamientoIcono('fechaHoraInicial')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('fechaHoraFinal')}
                  >
                    <div className="flex items-center gap-1">
                      F. Final
                      {getOrdenamientoIcono('fechaHoraFinal')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('imei')}
                  >
                    <div className="flex items-center gap-1">
                      IMEI SIM
                      {getOrdenamientoIcono('imei')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('placa')}
                  >
                    <div className="flex items-center gap-1">
                      Placa
                      {getOrdenamientoIcono('placa')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('estado')}
                  >
                    <div className="flex items-center gap-1">
                      Estado
                      {getOrdenamientoIcono('estado')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('tipoSimulacion')}
                  >
                    <div className="flex items-center gap-1">
                      Tipo
                      {getOrdenamientoIcono('tipoSimulacion')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrdenamiento('nombre_ruta')}
                  >
                    <div className="flex items-center gap-1">
                      Ruta
                      {getOrdenamientoIcono('nombre_ruta')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {simulacionesPagina.map((simulacion) => {
                  const estadoVisual = getEstadoVisual(simulacion)
                  const tipoVisual = getTipoSimulacionVisual(simulacion.tipoSimulacion)
                  
                  return (
                    <tr key={simulacion.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                        {simulacion.id}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {formatearFechaParaMostrar(simulacion.fechaLanzamiento)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {formatearFechaParaMostrar(simulacion.fechaHoraInicial)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {formatearFechaParaMostrar(simulacion.fechaHoraFinal)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                        {simulacion.imei}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                        {simulacion.placa}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${estadoVisual.bgColor} ${estadoVisual.color}`}>
                          <estadoVisual.icon className="w-3 h-3 mr-1" />
                          {estadoVisual.text}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tipoVisual.bgColor} ${tipoVisual.color}`}>
                          {tipoVisual.text}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                        <span className="truncate max-w-32 block" title={simulacion.nombre_ruta}>
                          {simulacion.nombre_ruta}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                        <div className="flex space-x-1">
                          {hasPermission('VER_SIMULACIONES_GPS') && (
                          <button
                            onClick={() => cargarSimulacion(simulacion.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Ver detalles"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          )}
                          {hasPermission('DUPLICAR_SIMULACION_GPS') && (
                          <button
                            onClick={() => duplicarSimulacion(simulacion.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Duplicar"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          )}
                          {hasPermission('ELIMINAR_SIMULACION_GPS') && (
                          <button
                            onClick={() => {
                              setDeleteId(simulacion.id)
                              setShowDeleteModal(true)
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación */}
      {!loading && simulacionesFiltradas.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, simulacionesFiltradas.length)} de {simulacionesFiltradas.length} resultados
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalles */}
      {showModal && selectedSimulacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Detalles de Simulación</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">ID</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.id}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de Ruta</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.nombre_ruta}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">IMEI Simulado</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.imei}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">IMEI Real</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.imei_real}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Placa</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.placa}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de Simulación</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.tipoSimulacion}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Lanzamiento</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedSimulacion.fechaLanzamiento)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha y Hora Inicial</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedSimulacion.fechaHoraInicial)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha y Hora Final</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedSimulacion.fechaHoraFinal)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.estado === '1' ? 'Activo' : 'Inactivo'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado Réplica</label>
                <p className="text-sm text-gray-900">{selectedSimulacion.estado_replica === '1' ? 'Activo' : 'Inactivo'}</p>
              </div>
              
              {/* Configuración de Plataformas */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Configuración de Plataformas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Plataforma Origen */}
                  <div className="bg-blue-50 p-3 rounded">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Origen</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">URL Tracker</label>
                        <p className="text-xs text-gray-900 break-all">{selectedSimulacion.tracker_url_origen || 'https://gps.tecnytte.com'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">API Key</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedSimulacion.api_key_origen || '4C992B2947DFF375E4D75337E9EB5AB1'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">User Token</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedSimulacion.user_token_origen || '29A51402D29359061D885496BBFD5CC7'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Plataforma Destino */}
                  <div className="bg-green-50 p-3 rounded">
                    <h4 className="text-sm font-semibold text-green-900 mb-2">Destino</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">URL Tracker</label>
                        <p className="text-xs text-gray-900 break-all">{selectedSimulacion.tracker_url_destino || 'https://gps.tecnytte.com'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">API Key</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedSimulacion.api_key_destino || '4C992B2947DFF375E4D75337E9EB5AB1'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">User Token</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedSimulacion.user_token_destino || '29A51402D29359061D885496BBFD5CC7'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Puntos de Ruta (JSON)</label>
                <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                  {/* {JSON.stringify(JSON.parse(selectedSimulacion.puntos), null, 2)} */}
                  {(() => {
                    try {
                      // Si el campo está vacío o solo tiene un punto, mostrar el valor original
                      if (!selectedSimulacion.puntos || selectedSimulacion.puntos.trim() === '' || selectedSimulacion.puntos.trim() === '.') {
                        return selectedSimulacion.puntos || 'Campo vacío';
                      }
                      // Intentar parsear JSON
                      const parsed = JSON.parse(selectedSimulacion.puntos);
                      return JSON.stringify(parsed, null, 2);
                    } catch (error) {
                      // Si falla el parse, mostrar el valor original
                      return selectedSimulacion.puntos || 'Error al parsear JSON';
                    }
                  })()}
                </pre>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => window.location.href = `/simulaciones-gps/editar/${selectedSimulacion.id}`}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Editar
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h2 className="text-xl font-bold">Confirmar Eliminación</h2>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Está seguro de que desea eliminar esta simulación? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={eliminarSimulacion}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionSimulacionesGPS 